/**
 * Revenue & Upsell Sequence Templates
 * Based on best practices from Slack, Zoom, Dropbox, HubSpot
 *
 * Key insights from billion-dollar companies:
 * - Upsell at moments of success, not frustration
 * - Show ROI and value before asking for upgrade
 * - Annual billing saves 20%+ = easy sell
 * - Enterprise features sell themselves with right timing
 */

import { SequenceTemplate } from "./types";

export const UPGRADE_NUDGE: SequenceTemplate = {
  id: "upgrade-nudge",
  name: "Smart Upgrade Nudge",
  type: "revenue",
  description: "Strategically encourage upgrades when users experience value",
  strategy: "Trigger when users hit 70% of plan limits or use premium features. Focus on unlocking potential, not pushing sales. Slack grew to $1B+ using this approach.",
  icon: "RocketOutlined",
  color: "#8b5cf6",
  expectedOutcomes: [
    "25-35% upgrade conversion",
    "Higher ARPU",
    "Positive upgrade experience",
    "Reduced support tickets about limits"
  ],
  bestPractices: [
    "Trigger at 70% usage, not 100%",
    "Show what they could achieve with upgrade",
    "Include ROI calculator or comparison",
    "Offer limited-time upgrade incentive",
    "Make downgrade easy to build trust"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: ["Free", "Optimizer"],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Value Recognition",
      subject: "{{first_name}}, you're getting great results!",
      suggestedTemplateId: "monthly-analytics",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Celebrate their success + hint at more potential",
      variables: [
        { key: "conversations_count", defaultValue: "{{total_conversations}}" },
        { key: "success_metric", defaultValue: "{{satisfaction_rate}}" }
      ]
    },
    {
      name: "Unlock More Potential",
      subject: "What {{plan_name}} users are achieving",
      suggestedTemplateId: "product-upsell",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Show success stories from upgraded users"
    },
    {
      name: "Special Upgrade Offer",
      subject: "Exclusive: Upgrade to {{plan_name}} - 20% off",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Time-limited upgrade incentive"
    }
  ],
  tags: ["revenue", "upgrade", "upsell", "growth"],
  bestFor: ["Freemium SaaS", "Usage-based products", "Tiered pricing"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const ANNUAL_CONVERSION: SequenceTemplate = {
  id: "annual-conversion",
  name: "Monthly to Annual Conversion",
  type: "revenue",
  description: "Convert monthly subscribers to annual plans",
  strategy: "Target engaged monthly users at renewal time. Annual plans have 3x better retention. Offer 2 months free (16% discount) - it's worth it for the LTV increase.",
  icon: "CalendarOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "15-25% annual conversion rate",
    "Improved cash flow",
    "3x better retention",
    "Higher customer LTV"
  ],
  bestPractices: [
    "Target users 7 days before monthly renewal",
    "Offer 2 months free (16% savings)",
    "Show total annual savings clearly",
    "Include money-back guarantee",
    "Highlight commitment-free cancellation"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 23, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: ["Optimizer", "Wizard"],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Annual Savings Preview",
      subject: "Save ${{savings_amount}} with annual billing",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Introduce annual option with clear savings",
      variables: [
        { key: "savings_amount", defaultValue: "{{annual_savings}}" },
        { key: "monthly_price", defaultValue: "{{current_monthly}}" },
        { key: "annual_price", defaultValue: "{{annual_monthly}}" }
      ]
    },
    {
      name: "Why Go Annual",
      subject: "3 reasons top users choose annual plans",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Build case for annual commitment"
    },
    {
      name: "Last Chance Annual Offer",
      subject: "Final reminder: Lock in your annual savings",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Urgency before monthly renewal"
    }
  ],
  tags: ["revenue", "annual", "conversion", "retention"],
  bestFor: ["Subscription SaaS", "Monthly billing products"],
  difficulty: "beginner",
  setupTime: "10 minutes"
};

export const ENTERPRISE_UPGRADE_PATH: SequenceTemplate = {
  id: "enterprise-upgrade",
  name: "Enterprise Upgrade Path",
  type: "revenue",
  description: "Guide growing teams toward enterprise plans",
  strategy: "When teams add 3+ users or hit growth metrics, introduce enterprise benefits. Focus on team collaboration, security, and dedicated support. Enterprise deals are 10x+ higher ARPU.",
  icon: "CrownOutlined",
  color: "#f59e0b",
  expectedOutcomes: [
    "Enterprise pipeline growth",
    "10x higher deal values",
    "Strategic account identification",
    "Sales team handoff"
  ],
  bestPractices: [
    "Trigger on team size or usage growth",
    "Lead with enterprise-only features",
    "Offer consultation call, not just upgrade",
    "Include security/compliance benefits",
    "Connect with dedicated success manager"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: ["Wizard", "Maverick"],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Growth Recognition",
      subject: "Your team is growing fast, {{first_name}}!",
      suggestedTemplateId: "monthly-analytics",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Recognize their growth + introduce enterprise"
    },
    {
      name: "Enterprise Features Preview",
      subject: "Unlock enterprise-grade features for your team",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Showcase enterprise-only capabilities"
    },
    {
      name: "Consultation Invitation",
      subject: "Let's discuss your team's needs",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Personal outreach for enterprise discussion"
    },
    {
      name: "Custom Solution Proposal",
      subject: "Custom enterprise proposal for {{company_name}}",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Tailored enterprise package offer"
    }
  ],
  tags: ["enterprise", "upgrade", "sales", "high-value"],
  bestFor: ["B2B SaaS", "Team collaboration tools", "Enterprise-ready products"],
  difficulty: "advanced",
  setupTime: "25 minutes"
};

export const ADDON_FEATURE_UPSELL: SequenceTemplate = {
  id: "addon-upsell",
  name: "Add-on Feature Upsell",
  type: "revenue",
  description: "Promote add-on features based on usage patterns",
  strategy: "When users frequently use a feature, suggest related premium add-ons. This is how Zoom grew their revenue per user by 40%.",
  icon: "AppstoreOutlined",
  color: "#3b82f6",
  expectedOutcomes: [
    "20-30% add-on attachment rate",
    "Increased ARPU",
    "Better feature discovery",
    "Personalized experience"
  ],
  bestPractices: [
    "Base recommendations on actual usage",
    "Show how add-on enhances their workflow",
    "Offer trial of premium feature",
    "Bundle complementary add-ons",
    "Time offers after successful feature use"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 14, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Usage-Based Recommendation",
      subject: "{{first_name}}, you might love this feature",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Introduce relevant add-on based on usage",
      variables: [
        { key: "feature_name", defaultValue: "{{recommended_feature}}" },
        { key: "usage_insight", defaultValue: "{{usage_pattern}}" }
      ]
    },
    {
      name: "Free Trial Offer",
      subject: "Try {{feature_name}} free for 7 days",
      suggestedTemplateId: "product-demo",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Offer trial of premium add-on"
    },
    {
      name: "Success Stories",
      subject: "How others use {{feature_name}} to get results",
      suggestedTemplateId: "product-feedback",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Social proof for add-on value"
    }
  ],
  tags: ["upsell", "add-on", "feature", "revenue"],
  bestFor: ["Modular products", "Add-on pricing models", "Feature-rich platforms"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const EXPANSION_REVENUE: SequenceTemplate = {
  id: "expansion-revenue",
  name: "Expansion Revenue Campaign",
  type: "revenue",
  description: "Grow revenue within existing accounts",
  strategy: "Target accounts showing growth signals: new team members, increased usage, new use cases. Expansion revenue is 3x cheaper than new customer acquisition.",
  icon: "RiseOutlined",
  color: "#10b981",
  expectedOutcomes: [
    "30%+ expansion revenue growth",
    "Lower CAC vs. new acquisition",
    "Deeper account penetration",
    "Champion identification"
  ],
  bestPractices: [
    "Monitor growth signals in accounts",
    "Reach out to power users",
    "Offer team or department rollout",
    "Provide volume discounts",
    "Assign dedicated success manager"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 60, unit: "days" }
  },
  segmentation: {
    behavior: {
      usagePattern: "active",
      engagementLevel: "high"
    },
    demographic: {
      subscriptionTier: ["Wizard", "Maverick"],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Account Review",
      subject: "Your {{product_name}} impact: A 60-day review",
      suggestedTemplateId: "performance-report",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "Show account value + growth opportunity"
    },
    {
      name: "Team Expansion Offer",
      subject: "Bring your whole team to {{product_name}}",
      suggestedTemplateId: "product-upsell",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Encourage team-wide adoption"
    },
    {
      name: "Success Manager Introduction",
      subject: "Meet your dedicated success manager",
      suggestedTemplateId: "invitation",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Personal touch for expansion discussion"
    }
  ],
  tags: ["expansion", "revenue", "growth", "accounts"],
  bestFor: ["B2B SaaS", "Team products", "Enterprise sales"],
  difficulty: "advanced",
  setupTime: "20 minutes"
};

export const PRICING_CHANGE_COMMUNICATION: SequenceTemplate = {
  id: "pricing-change",
  name: "Pricing Change Communication",
  type: "revenue",
  description: "Communicate pricing changes while retaining customers",
  strategy: "Price increases done right can improve retention. Give notice, explain value, offer loyalty rewards. Netflix and Spotify maintain 95%+ retention through price increases.",
  icon: "DollarOutlined",
  color: "#ef4444",
  expectedOutcomes: [
    "95%+ retention through price change",
    "Minimal support tickets",
    "Positive brand perception",
    "Grandfather loyal customers"
  ],
  bestPractices: [
    "Give 30+ days advance notice",
    "Explain the 'why' - new features, costs",
    "Offer legacy pricing for loyal customers",
    "Provide annual lock-in option",
    "Make it easy to cancel (builds trust)"
  ],
  trigger: {
    eventType: "timeAfterSignup",
    delay: { value: 0, unit: "minutes" }
  },
  segmentation: {
    behavior: {
      usagePattern: "any",
      engagementLevel: "any"
    },
    demographic: {
      subscriptionTier: [],
      subscriptionStatus: ["active"]
    }
  },
  emails: [
    {
      name: "Advance Notice",
      subject: "Important update about your {{product_name}} subscription",
      suggestedTemplateId: "product-announcement",
      delayAfterPrevious: { value: 0, unit: "minutes" },
      purpose: "30-day advance notice with explanation",
      variables: [
        { key: "old_price", defaultValue: "{{current_price}}" },
        { key: "new_price", defaultValue: "{{new_price}}" },
        { key: "effective_date", defaultValue: "{{change_date}}" }
      ]
    },
    {
      name: "Value Reinforcement",
      subject: "Here's what's new in {{product_name}}",
      suggestedTemplateId: "product-launch",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Showcase new features justifying price"
    },
    {
      name: "Loyalty Offer",
      subject: "{{first_name}}, thank you for being with us",
      suggestedTemplateId: "promotion",
      delayAfterPrevious: { value: 7, unit: "days" },
      purpose: "Exclusive loyalty discount or lock-in"
    },
    {
      name: "Final Reminder",
      subject: "Reminder: Pricing update on {{effective_date}}",
      suggestedTemplateId: "product-reminder",
      delayAfterPrevious: { value: 14, unit: "days" },
      purpose: "Final notice before change"
    }
  ],
  tags: ["pricing", "retention", "communication", "change"],
  bestFor: ["All SaaS products", "Subscription businesses"],
  difficulty: "intermediate",
  setupTime: "15 minutes"
};

export const REVENUE_TEMPLATES = [
  UPGRADE_NUDGE,
  ANNUAL_CONVERSION,
  ENTERPRISE_UPGRADE_PATH,
  ADDON_FEATURE_UPSELL,
  EXPANSION_REVENUE,
  PRICING_CHANGE_COMMUNICATION
];
